import { useEffect } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

import useSetLoading from '@/hooks/useSetLoading';
import alertState from '@/states/alertState';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';

import ExternalLink from '@/components/atoms/ExternalLink';
import ExternalLinkButton from '@/components/atoms/ExternalLinkButton';
import Heading from '@/components/atoms/Heading';
import useGetSoNetMailInfo from '@/hooks/api/useGetSoNetMailInfo';
import SoNetMailInfoState from '@/states/soNetMailInfoState';

type Props = {
  anchor: string;
};

/**
 * So-net メールシステム情報コンポーネント
 * @param {anchor}
 * @returns So-net メールシステム情報コンポーネント or null
 */
const SoNetMailInfo = ({ anchor }: Props) => {
  // So-net メールシステム情報 state
  const [soNetMailInfo, setSoNetMailInfo] = useRecoilState(SoNetMailInfoState);
  // So-net メールシステム情報 API Hook
  const { execute: getSoNetMailInfo } = useGetSoNetMailInfo();
  // ローディング
  const { startLoading, finishLoading } = useSetLoading();
  // アラート
  const setAlert = useSetRecoilState(alertState);

  // 初期表示
  useEffect((): void => {
    callAPIGetSoNetMailInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // So-net メールシステム情報取得API呼び出し
  const callAPIGetSoNetMailInfo = async () => {
    try {
      startLoading(ApiUrl.Read.SONET_MAIL_INFO);
      const { data } = await getSoNetMailInfo();
      // 取得した値をセット
      setSoNetMailInfo({ ...data });
    } catch (error: any) {
      if (error.response.status === 403) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.SONET_MAIL_INFO);
    }
  };

  // メール情報がなければ表示しない
  if (!soNetMailInfo.mail_addr) return null;

  // メールアカウントステータスが「停止」、「削除」の場合は表示しない
  if (soNetMailInfo.status === '1' || soNetMailInfo.status === '3') return null;

  return (
    <div className='m-box-02' id={anchor}>
      <div className='m-box-02__inner'>
        {/** So-net メール情報 */}
        <Heading as='h2' icon='mail'>
          So-net メール情報
        </Heading>
        <p className='sonetMailImportText'>
          一定期間ご利用がない場合には、So-netメールアドレスは削除されます。詳しくは
          <ExternalLink
            href='https://www.nuro.jp/news_release/20230912/'
            ariaLabel='ご利用履歴のないメールアドレスの削除について'
          >
            こちら
          </ExternalLink>
          のお知らせをご覧ください。
        </p>
        <div className='m-table'>
          <table>
            <colgroup>
              <col className='w-30' />
              <col />
            </colgroup>
            <tbody>
              <tr>
                <th className='m-table__th'>So-net メールアドレス</th>
                <td className='m-table__td' data-testid='sonet-mail-addr'>
                  {soNetMailInfo.mail_addr}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>So-net WEBメール</th>
                <td className='m-table__td'>
                  <ExternalLink
                    href={Constants.SoNetMail.URL}
                    ariaLabel='So-net WEBメールはこちら'
                    data-testid='sonet-mail-url'
                  >
                    {Constants.SoNetMail.URL}
                  </ExternalLink>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className='m-button align-center'>
          <ExternalLinkButton
            href={`${process.env.NEXT_PUBLIC_SONET_MAIL_SETTING_URL}`}
            variant='secondary'
          >
            So-net メール設定
          </ExternalLinkButton>
        </div>
      </div>
    </div>
  );
};

export default SoNetMailInfo;
